import { FC } from 'react';
import { Breakpoints, useBreakpoint, useTranslation, useSession, useMasterData } from '@nextgen-web-framework/all';
import { ConfigService } from '@nextgen-web-framework/core';
import {
  MobileBottomNavigation,
  BottomNavigationItemType,
  transformMobileBottomNavigation,
} from '@commerce-webcomponents/ui';
import { Config } from '@com.amway.commerce/webshop-configs-v1-prod/Schema';
import SiteHeader from '../siteHeader/SiteHeader';
import type { HeaderContainerPropsTypes } from './types/propsTypes';

const HeaderContainer: FC<HeaderContainerPropsTypes> = ({
  activeRoute,
  header,
  onMePanelClick,
  tabletScrolled,
  headerBody,
  hasTabletScrolledHeaderBody,
  hasStickyBottomBar,
  onSuggestionClick,
  langHandler,
  locale,
  cartListId,
}) => {
  const configs = ConfigService.getConfigs();
  const ASSETS_BASE_PATH = configs.NEXT_ASSETS_BASE_PATH;
  const viewPortType = useBreakpoint() as Breakpoints;
  const { isAnonymous } = useSession();
  const t = useTranslation();
  const webShopConfigs = useMasterData<Config>('WebshopConfigs') as Config;
  const isSopMegaMenuEnabled = webShopConfigs?.sopMegaMenuEnabled;
  const isAccountLoggedIn = !isAnonymous();

  return (
    <div data-testid="header-container-test-id">
      {viewPortType && (
        <>
          {[Breakpoints.XS, Breakpoints.SM].includes(viewPortType) ? (
            <MobileBottomNavigation
              activeRoute={activeRoute}
              navigationItems={transformMobileBottomNavigation(
                header?.mobile_bottom_navigation_menu,
                isAnonymous(),
                isSopMegaMenuEnabled,
                [
                  {
                    label: t('web.bottomTabNavigation.home'),
                    type: BottomNavigationItemType.Image,
                    image: {
                      activeImageUrl: `${ASSETS_BASE_PATH}/icons/AmwayActive.svg`,
                      inactiveImageUrl: `${ASSETS_BASE_PATH}/icons/AmwayWhite.svg`,
                    },
                    url: '/',
                  },
                  {
                    label: t('web.bottomTabNavigation.shop'),
                    type: BottomNavigationItemType.Image,
                    image: {
                      activeImageUrl: `${ASSETS_BASE_PATH}/icons/store-Solid.svg`,
                      inactiveImageUrl: `${ASSETS_BASE_PATH}/icons/store-Regular.svg`,
                    },
                    url: '/shopping',
                  },
                  {
                    label: t('web.bottomTabNavigation.offers'),
                    type: BottomNavigationItemType.Image,
                    image: {
                      inactiveImageUrl: `${ASSETS_BASE_PATH}/icons/tags-Regular.svg`,
                      activeImageUrl: `${ASSETS_BASE_PATH}/icons/tags-Solid.svg`,
                    },
                    url: '/offers',
                  },
                  ...(isSopMegaMenuEnabled
                    ? [
                        {
                          label: t('web.bottomTabNavigation.sop'),
                          type: BottomNavigationItemType.Image,
                          image: {
                            inactiveImageUrl: `${ASSETS_BASE_PATH}/icons/arrows-rotate-Regular.svg`,
                            activeImageUrl: `${ASSETS_BASE_PATH}/icons/arrows-rotate-Solid.svg`,
                          },
                          url: '/sop',
                        },
                      ]
                    : []),
                  {
                    label: isAnonymous() ? t('web.bottomTabNavigation.account') : t('web.bottomTabNavigation.me'),
                    type: BottomNavigationItemType.Image,
                    image: {
                      inactiveImageUrl: `${ASSETS_BASE_PATH}/icons/circle-user-Regular.svg`,
                      activeImageUrl: `${ASSETS_BASE_PATH}/icons/circle-user-Solid.svg`,
                    },
                    url: '/account',
                    isLoggedIn: isAccountLoggedIn,
                  },
                ],
              )}
              hasStickyBottomBar={hasStickyBottomBar}
            />
          ) : (
            <SiteHeader
              data={header}
              tabletScrolled={tabletScrolled}
              onMePanelClick={onMePanelClick}
              hasTabletScrolledHeaderBody={hasTabletScrolledHeaderBody}
              headerBody={headerBody}
              onSuggestionClick={onSuggestionClick}
              cartListId={cartListId}
              activeRoute={activeRoute}
              langHandler={langHandler}
              locale={locale}
              isLoggedIn={isAccountLoggedIn}
            />
          )}
        </>
      )}
    </div>
  );
};

export default HeaderContainer;
